// src/hooks/useSidebarCounts.ts
/**
 * Sidebar badge counts, live over the dashboards WebSocket service.
 *
 * Replaces the old 60 second poll, which pulled `listView({ take: 10000 })` just to produce two
 * integers and could not serve the other badges at all. Two hubs push here instead:
 *
 * - **`BadgeSnapshot`** is the full authoritative set. It arrives on connect, on reconnect, and every
 *   300 seconds as a drift backstop. It **replaces** the domain's slice, never merges into it: a key
 *   that vanished means the status lookup row was deactivated, and merging would keep a dead count alive.
 * - **`BadgeDelta`** is a sparse signed patch. It **adds**, clamped at zero, and leaves unmentioned keys
 *   untouched.
 *
 * ⚠️ **Absent is not zero.** A key that is present and `0` means "you may see this and there are none";
 * a key that is missing means "not yours to see". `MainSidebar` branches on exactly that to decide
 * whether to render a badge, so never default a missing key to 0 on the way out.
 *
 * ⚠️ **Mount once.** This opens two WebSockets. Always consume it through `useSidebarCountsContext()`,
 * which is provided once in `main.tsx`, rather than calling this hook in individual components.
 *
 * ---
 *
 * ## Why this drives `DomainDashboardClient` directly instead of using `useDomainDashboard`
 *
 * The SDK hook cannot survive React StrictMode, which this app enables (`main.tsx`). Its lifecycle is:
 *
 * ```js
 * useEffect(() => {
 *   if (autoConnect) connect().then(() => autoSubscribe && subscribe()).catch(…);
 *   return () => { disconnect(); };          // always, even when autoConnect is false
 * }, [autoConnect]);
 * ```
 *
 * StrictMode runs mount, cleanup, mount. The cleanup fires **while the first negotiation is still in
 * flight**, which aborts it with *"The connection was stopped during negotiation"*. That alone would be
 * survivable, the second mount would reconnect. What makes it terminal is that `disconnect()` only sets
 * `clientRef.current = null` **after** awaiting `stop()`, so the second `connect()` still sees the old
 * client, skips constructing a new one, and calls `connect()` on the instance that is being torn down.
 * Both attempts fail and no badge ever arrives.
 *
 * Owning the clients here fixes it because they are **created inside the effect**, not stored across
 * runs. StrictMode's cleanup disposes set one, and the re-run builds a clean set two that connects
 * normally. In production, where StrictMode does not double-invoke, this connects exactly once.
 *
 * Revisit if the SDK's hook is fixed; the rest of the contract (`badgeSnapshot` / `badgeDelta` events,
 * `SubscribeToDashboard`, `RequestBadgeSnapshot`) is used exactly as the hook uses it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DomainDashboardClient,
  useCCMS,
  useAuth,
  ConnectionState,
  type BadgeSnapshotMessage,
  type BadgeDeltaMessage,
} from '@actschurch/ccms-sdk';
import { CARE_DASHBOARD, nsBadgeKey as ns } from './careBadgeKeys';

/** Flat, namespaced count map, e.g. `{ 'accounselling.leader.cases.pending': 2 }`. */
export type SidebarCounts = Record<string, number>;

type HubStatus = { connected: boolean; error: Error | null };

const IDLE_STATUS: HubStatus = { connected: false, error: null };

/** Server-side metrics refresh, in seconds. Badges are event-driven; this governs the metrics channel. */
const REFRESH_INTERVAL_SECONDS = 30;

/**
 * Dev-only tracing. Badge failures are otherwise completely silent: the hub can refuse the connection,
 * or it can connect happily and send keys that nothing maps to, and both look identical from the
 * sidebar, which is simply an absence of badges. Set `localStorage.debugBadges = '1'` to see the traffic.
 */
const traceEnabled = () => {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem('debugBadges') === '1';
  } catch {
    return false;
  }
};

const trace = (...args: unknown[]) => {
  if (traceEnabled()) console.info('[sidebar-counts]', ...args);
};

/**
 * Pulls the count map out of a badge message.
 *
 * The SDK types it as an envelope, `{ domain, snapshot }` and `{ domain, reason, deltas }`, and the
 * client hands the raw hub argument straight through. Accepting a **bare map** as well costs nothing and
 * removes a whole class of silent failure: if the hub sent the counts at the top level instead, reading
 * `.snapshot` would yield `undefined`, which as a snapshot means "clear every badge".
 *
 * Returns `null` when no map can be found, which callers treat as "ignore this message" rather than
 * "the answer is nothing".
 */
function readCountMap(message: unknown, field: 'snapshot' | 'deltas'): Record<string, number> | null {
  if (!message || typeof message !== 'object') return null;

  const envelope = (message as Record<string, unknown>)[field];
  if (envelope && typeof envelope === 'object') {
    return Object.fromEntries(
      Object.entries(envelope as Record<string, unknown>)
        .filter((entry): entry is [string, number] => typeof entry[1] === 'number'),
    );
  }

  // No envelope key. Fall back to the numeric entries at the top level, ignoring the metadata fields
  // (`domain`, `reason`, `refId`, `taskId`) that ride alongside them.
  const METADATA = new Set(['domain', 'reason', 'refId', 'taskId']);
  const flat = Object.entries(message as Record<string, unknown>)
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number' && !METADATA.has(entry[0]));

  return flat.length > 0 ? Object.fromEntries(flat) : null;
}

export function useSidebarCounts() {
  const { config } = useCCMS();
  const { state: authState } = useAuth();

  /**
   * ⚠️ **Do not remove this gate.**
   *
   * `SidebarCountsProvider` sits above `<App/>` in `main.tsx`, so this hook mounts on the **login page**,
   * before any token exists. Connecting then gets `[SignalR] Token unavailable` followed by the hub
   * closing with `HubException: Unauthorized: Authentication required`. Because the connect effect's
   * dependencies are otherwise all stable, it never retried, so the sockets stayed dead for the whole
   * session and no badge ever appeared until a full page reload.
   *
   * Keying the effect on this makes login the trigger to connect and logout the trigger to tear down.
   */
  const isAuthenticated = authState.isAuthenticated;

  const [counts, setCounts] = useState<SidebarCounts>({});
  const [counsellingStatus, setCounsellingStatus] = useState<HubStatus>(IDLE_STATUS);
  const [coursesStatus, setCoursesStatus] = useState<HubStatus>(IDLE_STATUS);

  /** The live clients, for the imperative resync below. The effect owns their lifecycle. */
  const clientsRef = useRef<DomainDashboardClient[]>([]);

  // Replaces this domain's slice. Every `${domain}.` key is dropped first so a key that disappeared
  // from the new snapshot disappears from state too. The other domain is left untouched.
  //
  // ⚠️ This is the **only** thing that can remove counts, so it is also the only thing that can make
  // working badges vanish. Hence the two guards in `readCountMap` and below.
  const applySnapshot = useCallback((domain: string, message: unknown) => {
    const snapshot = readCountMap(message, 'snapshot');
    trace(`snapshot ${domain}`, { parsed: snapshot, rawMessage: message });

    if (snapshot === null) {
      console.warn(
        `[sidebar-counts] ${domain}: BadgeSnapshot had no readable count map, ignoring it rather than ` +
        'clearing the badges. Payload:', message,
      );
      return;
    }

    setCounts((prev) => {
      const held = Object.keys(prev).filter((k) => k.startsWith(`${domain}.`));

      // An empty snapshot where we already hold keys means "you may now see nothing", which cannot
      // happen mid-session: a user's roles do not change under them. Far likelier the server sent an
      // unscoped or unimplemented response, so keep what we have and say so. A genuinely empty first
      // snapshot still applies, because there is nothing to lose.
      if (Object.keys(snapshot).length === 0 && held.length > 0) {
        console.warn(
          `[sidebar-counts] ${domain}: ignored an empty BadgeSnapshot that would have cleared ` +
          `${held.length} existing count(s). This usually means the server answered without ` +
          'rejoining the connection to its groups.',
        );
        return prev;
      }

      const next: SidebarCounts = {};
      for (const [k, v] of Object.entries(prev)) {
        if (!k.startsWith(`${domain}.`)) next[k] = v;
      }
      for (const [k, v] of Object.entries(snapshot)) next[ns(domain, k)] = v;
      return next;
    });
  }, []);

  // Adds, clamped at zero. Keys not mentioned in the delta are left alone.
  const applyDelta = useCallback((domain: string, message: unknown) => {
    const deltas = readCountMap(message, 'deltas');
    trace(`delta ${domain}`, { parsed: deltas, rawMessage: message });

    if (deltas === null) {
      console.warn(`[sidebar-counts] ${domain}: BadgeDelta had no readable count map. Payload:`, message);
      return;
    }

    setCounts((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(deltas)) {
        const key = ns(domain, k);
        next[key] = Math.max(0, (next[key] ?? 0) + v);
      }
      return next;
    });
  }, []);

  // Primitives and a ref, so a new `config` object identity can never restart the sockets.
  const baseUrl = config.dashboardsUrl || config.baseUrl;
  const getTokenRef = useRef(config.getToken);
  getTokenRef.current = config.getToken;

  useEffect(() => {
    if (!baseUrl) {
      console.error('[sidebar-counts] no dashboards URL configured; badges are disabled.');
      return undefined;
    }

    // Signed out: nothing to subscribe to, and the hub would reject us anyway. Clear any counts left
    // over from the previous session so a different user cannot briefly see the last one's numbers.
    if (!isAuthenticated) {
      trace('not authenticated yet, holding off');
      setCounts({});
      setCounsellingStatus(IDLE_STATUS);
      setCoursesStatus(IDLE_STATUS);
      return undefined;
    }

    let disposed = false;

    /**
     * Builds a client, wires its handlers, connects and subscribes.
     *
     * `domain as never` sidesteps the SDK's closed `DashboardDomain` union, which still lacks the two
     * care hubs. Type-only: the string is interpolated straight into the hub URL. See careBadgeKeys.ts.
     */
    const startHub = (domain: string, setStatus: (s: HubStatus) => void) => {
      const client = new DomainDashboardClient(
        baseUrl,
        domain as never,
        async () => (getTokenRef.current ? await getTokenRef.current() : null),
      );

      // Distinguishes a *return* to Connected from the first arrival at it.
      let everSubscribed = false;

      const subscribe = () => client.subscribe(undefined, REFRESH_INTERVAL_SECONDS);

      client.onStateChange((state, err) => {
        if (disposed) return;
        setStatus({ connected: state === ConnectionState.Connected, error: err ?? null });
        trace(`state ${domain}`, state, err?.message ?? '');

        // ⚠️ SignalR's auto-reconnect gives a **new connection**, so the previous
        // `SubscribeToDashboard` is gone and any deltas sent while we were away were missed.
        // `BaseSignalRClient.onreconnected` does not re-subscribe or resync, despite
        // `requestBadgeSnapshot`'s JSDoc claiming the SDK reconnect handler calls it. It does not.
        // Owning the client means owning that.
        if (state === ConnectionState.Connected && everSubscribed) {
          trace(`reconnected ${domain}, resyncing`);
          void (async () => {
            try {
              await subscribe();
              await client.requestBadgeSnapshot();
            } catch (e) {
              if (!disposed) console.error(`[sidebar-counts] ${domain} resync after reconnect failed:`, e);
            }
          })();
        }
      });

      client.on<BadgeSnapshotMessage>('badgeSnapshot', (m) => {
        if (!disposed) applySnapshot(domain, m);
      });

      client.on<BadgeDeltaMessage>('badgeDelta', (m) => {
        if (!disposed) applyDelta(domain, m);
      });

      void (async () => {
        try {
          await client.connect();
          // StrictMode's cleanup may have run while we were negotiating. Bail rather than subscribing
          // on a client that is already being torn down.
          if (disposed) return;
          await subscribe();
          everSubscribed = true;
          trace(`subscribed ${domain}`);
        } catch (e) {
          // An abort caused by our own cleanup is expected once per mount under StrictMode, and is not
          // worth shouting about. Anything else is a real failure the user needs to see.
          if (disposed) {
            trace(`connect aborted by cleanup ${domain}`);
            return;
          }
          const error = e instanceof Error ? e : new Error(String(e));
          console.error(`[sidebar-counts] ${domain} hub failed to connect:`, error);
          setStatus({ connected: false, error });
        }
      })();

      return client;
    };

    const counselling = startHub(CARE_DASHBOARD.counselling, setCounsellingStatus);
    const courses = startHub(CARE_DASHBOARD.courses, setCoursesStatus);
    clientsRef.current = [counselling, courses];

    return () => {
      disposed = true;
      clientsRef.current = [];
      void counselling.disconnect();
      void courses.disconnect();
    };
  }, [baseUrl, isAuthenticated, applySnapshot, applyDelta]);

  /**
   * Kept under the old name so existing callers (`EnrolmentsListView`, `EnrolmentCourseGrid`,
   * `PendingEnrolmentModal`, and the rest) keep compiling. It is now a cheap resync, a request for the
   * authoritative snapshot, rather than a 10 000 row REST pull. Most call sites can go once deltas are
   * trusted, since the user's own action already moves the badge.
   */
  const refetch = useCallback(() => {
    clientsRef.current.forEach((client) => {
      if (client.isConnected()) void client.requestBadgeSnapshot();
    });
  }, []);

  const loading = !counsellingStatus.connected && !coursesStatus.connected;

  useEffect(() => { trace('counts now', counts); }, [counts]);

  return {
    counts,
    loading,
    refetch,
    /** Per-hub connection state and error, for diagnosing an empty badge set. */
    status: { counselling: counsellingStatus, courses: coursesStatus },
  };
}
